import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@material-ui/core";
import MuiAccordion from "@material-ui/core/Accordion";
import MuiAccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Paper from "@material-ui/core/Paper";
import { withStyles } from "@material-ui/core/styles";
import deepPurple from "@material-ui/core/colors/deepPurple";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import LockIcon from "@material-ui/icons/Lock";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import Loader from "../../shared/Loader";
import OutlinedButton from "../../OutlinedButton";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { useModals } from "../../../hooks/useModals";
import { useAppContext } from "../../../api/AppContext";
import {
  deleteHrDocument,
  listHrDocuments,
  updateHrDocument,
} from "../../../api";

const Accordion = withStyles({
  root: {
    "&:before": {
      height: 0,
    },
    backgroundColor: "transparent",
    boxShadow: "none",
    width: "100%",
  },
})(MuiAccordion);

const AccordionSummary = withStyles({
  content: {
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
  },
})(MuiAccordionSummary);

const UNKNOWN_ENTITY_KEY = "__unknown__";

const getDocTitle = (doc) =>
  doc?.textDefault?.title || doc?.internalName || doc?.customName || "—";

const HrDocumentsList = () => {
  const history = useHistory();
  const modals = useModals();
  const { enqueueSnackbar } = useSnackbar();
  const { fetchWelfareSelector, welfareSelectors } = useAppContext();

  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [expanded, setExpanded] = useLocalStorage(
    "HrDocumentsList.Expanded",
    {}
  );

  const handleExpandClick = useCallback(
    (key) => () => {
      setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [setExpanded]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const rows = await listHrDocuments();
      setDocuments(Array.isArray(rows) ? rows : []);
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to load HR documents", {
        variant: "error",
      });
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!welfareSelectors.entities?.length) {
      fetchWelfareSelector("entities");
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedDocuments = useMemo(() => {
    const entities = welfareSelectors.entities || [];
    const groups = documents.reduce((acc, doc) => {
      const entityId = doc.conditions?.entity || UNKNOWN_ENTITY_KEY;
      if (!acc[entityId]) {
        const entity = entities.find((e) => e.id === entityId);
        acc[entityId] = {
          key: entityId,
          name:
            entity?.displayname ||
            (entityId === UNKNOWN_ENTITY_KEY ? "No entity" : "Unknown entity"),
          documents: [],
        };
      }
      acc[entityId].documents.push(doc);
      return acc;
    }, {});

    return Object.values(groups)
      .map((group) => ({
        ...group,
        documents: [...group.documents].sort((a, b) =>
          getDocTitle(a).localeCompare(getDocTitle(b))
        ),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [documents, welfareSelectors.entities]);

  const handleCreate = useCallback(() => {
    history.push("/welfares/hr-documents/new");
  }, [history]);

  const handleEdit = useCallback(
    ({ id }) => {
      history.push(`/welfares/hr-documents/${id}`);
    },
    [history]
  );

  const handleDelete = useCallback(
    (doc) => {
      modals.openConfirmation({
        text: `You want to delete document "${getDocTitle(doc)}"`,
        onConfirm: async () => {
          try {
            await deleteHrDocument(doc.id);
            enqueueSnackbar("Document deleted", { variant: "success" });
            await load();
          } catch (err) {
            enqueueSnackbar(err?.message || "Delete failed", {
              variant: "error",
            });
          }
        },
      });
    },
    [enqueueSnackbar, load, modals]
  );

  const handleToggle = useCallback(
    async (doc, field) => {
      try {
        const next = !doc[field];
        await updateHrDocument(doc.id, { [field]: next });
        setDocuments((prev) =>
          prev.map((d) => (d.id === doc.id ? { ...d, [field]: next } : d))
        );
      } catch (err) {
        enqueueSnackbar(err?.message || "Update failed", {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar]
  );

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      {isLoading ? (
        <Loader />
      ) : (
        <Grid container direction="column" spacing={4}>
          <Grid item>
            <Grid container direction="column" spacing={1}>
              <Grid item>
                <Typography variant="h6">HR Documents</Typography>
              </Grid>
              <Grid item>
                <Typography color="textSecondary" variant="body2">
                  Use this section to add and maintain HR documents, grouped by
                  legal entity.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Button
              color="primary"
              endIcon={<AddIcon />}
              variant="contained"
              onClick={handleCreate}
            >
              Add new document
            </Button>
          </Grid>
          <Grid item>
            {!groupedDocuments.length && (
              <Typography color="textSecondary" variant="body2">
                No HR documents yet.
              </Typography>
            )}
            <List dense>
              {groupedDocuments.map((group) => (
                <Accordion
                  key={group.key}
                  expanded={Boolean(expanded[group.key])}
                  onChange={handleExpandClick(group.key)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">
                      {group.name} ({group.documents.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper style={{ width: "100%" }} variant="outlined">
                      <Box p={2}>
                        <List>
                          {group.documents.map((doc, i) => (
                            <ListItem
                              key={doc.id}
                              style={{
                                ...(!doc.isPublished && {
                                  backgroundColor: deepPurple[50],
                                }),
                                ...(i < group.documents.length - 1 && {
                                  borderBottom: "1px solid gray",
                                }),
                              }}
                            >
                              <ListItemText
                                primary={getDocTitle(doc)}
                                secondary={
                                  <>
                                    {doc.internalName &&
                                      doc.internalName !==
                                        doc.textDefault?.title && (
                                        <span style={{ display: "block" }}>
                                          {doc.internalName}
                                        </span>
                                      )}
                                    <span
                                      style={{
                                        alignItems: "center",
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 4,
                                        marginTop: 2,
                                      }}
                                    >
                                      {doc.category?.name && (
                                        <Chip
                                          label={doc.category.name}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                      {doc.fileType && (
                                        <Chip
                                          label={String(
                                            doc.fileType
                                          ).toUpperCase()}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                      {(doc.files || []).length > 0 && (
                                        <Chip
                                          label={`${doc.files.length} language${
                                            doc.files.length > 1 ? "s" : ""
                                          }`}
                                          size="small"
                                          variant="outlined"
                                        />
                                      )}
                                    </span>
                                  </>
                                }
                                secondaryTypographyProps={{
                                  component: "div",
                                }}
                              />
                              <ListItemSecondaryAction
                                style={{
                                  alignItems: "center",
                                  display: "flex",
                                }}
                              >
                                <Tooltip
                                  title={
                                    doc.isPublished
                                      ? "Published (click to unpublish)"
                                      : "Unpublished (click to publish)"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      onClick={() =>
                                        handleToggle(doc, "isPublished")
                                      }
                                    >
                                      {doc.isPublished ? (
                                        <VisibilityIcon />
                                      ) : (
                                        <VisibilityOffIcon />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    doc.isUserAccessible
                                      ? "User accessible (click to make agent-only)"
                                      : "Agent only (click to make user accessible)"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      onClick={() =>
                                        handleToggle(doc, "isUserAccessible")
                                      }
                                    >
                                      {doc.isUserAccessible ? (
                                        <LockOpenIcon />
                                      ) : (
                                        <LockIcon />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <span style={{ margin: "0 0.5rem" }}>
                                    <OutlinedButton
                                      onClick={() => handleEdit(doc)}
                                    >
                                      Edit
                                    </OutlinedButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <span>
                                    <IconButton
                                      onClick={() => handleDelete(doc)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default HrDocumentsList;
